import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChange, getUserData } from './services/userService';
import { getUserActiveSubscription } from './services/subscriptionService';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Register from './pages/Register';
import JobSeekerDashboard from './pages/JobSeekerDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import CompanyProfileManagement from './pages/CompanyProfileManagement';
import CompaniesList from './pages/CompaniesList';
import CompanyDetail from './pages/CompanyDetail';
import PostJob from './pages/PostJob';
import ManageJobs from './pages/ManageJobs';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import SavedJobs from './pages/SavedJobs';
import PublicHome from './pages/PublicHome';
import PublicJobsPage from './pages/PublicJobsPage';
import SeekerProfile from './pages/SeekerProfile';
import ApplicationHistory from './pages/ApplicationHistory';
import JobApplications from './pages/JobApplications';
import EmployerApplications from './pages/EmployerApplications';

import LoadingSpinner from './components/LoadingSpinner';
import ManageUsers from './pages/ManageUsers';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TeamDashboard from './pages/TeamDashboard';
import TeamUsers from './pages/TeamUsers';
import TeamCompanies from './pages/TeamCompanies';
import TeamJobs from './pages/TeamJobs';
import ProtectedRoute from './routes/ProtectedRoute';
import DashboardRedirect from './components/DashboardRedirect';
import CashfreeReturnHandler from './components/CashfreeReturnHandler';
import SuperAdminPackages from './pages/SuperAdminPackages';
import AgentDashboard from './pages/AgentDashboard';
import SuperAdminJobs from './pages/SuperAdminJobs';
import SuperAdminCompanies from './pages/SuperAdminCompanies';
import SuperAdminPageApproval from './pages/SuperAdminPageApproval';
import PackagesPage from './pages/PackagesPage';
import MarketingPage from './pages/MarketingPage';
import MySubscription from './pages/MySubscription';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import ShippingPolicy from './pages/ShippingPolicy';
import RefundPolicy from './pages/RefundPolicy';
import Contact from './pages/Contact';
import About from './pages/About';
import AgentDetailsManagement from './pages/AgentDetailsManagement';
import NotificationSettings from './components/NotificationSettings';
import DeleteAccount from './pages/DeleteAccount';
import LandingPage from './pages/LandingPage';
import SeekerDatabase from './pages/SeekerDatabase';
import AdminUserProfile from './pages/AdminUserProfile';
import BillingHistory from './pages/BillingHistory';
import DisclaimerPolicy from './pages/DisclaimerPolicy';



import FranchiseLogin from './pages/Franchise/FranchiseLogin';
import FranchiseDashboard from './pages/Franchise/FranchiseDashboard';
import SuperAdminFranchiseManagement from './pages/SuperAdminFranchiseManagement';
import FranchiseUserList from './pages/Franchise/FranchiseUserList';
import FranchiseAddEmployer from './pages/Franchise/FranchiseAddEmployer';
import FranchiseCompanies from './pages/Franchise/FranchiseCompanies';
import FranchiseJobs from './pages/Franchise/FranchiseJobs';
import FranchiseProfile from './pages/Franchise/FranchiseProfile';
import FranchiseApplications from './pages/Franchise/FranchiseApplications';

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserDataAndSubscription = async (currentUser) => {
    if (!currentUser) return null;
    const userDataResult = await getUserData(currentUser.uid);
    if (userDataResult.success) {
      const fetchedUserData = userDataResult.data || {};
      const subscriptionResult = await getUserActiveSubscription(currentUser.uid);
      if (subscriptionResult.success && subscriptionResult.subscription) {
        fetchedUserData.paymentId = true;
      }
      return fetchedUserData;
    }
    return null;
  };

  const refreshUserData = async () => {
    if (user) {
      const updatedData = await fetchUserDataAndSubscription(user);
      if (updatedData) {
        setUserData(updatedData);
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (currentUser) => {
      if (currentUser) {
        const fetchedUserData = await fetchUserDataAndSubscription(currentUser);
        setUserData(fetchedUserData);
        setUser(currentUser);
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <CashfreeReturnHandler user={user} userData={userData} onPaymentVerified={refreshUserData} />
        <Navbar user={user} userData={userData} />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/franchise/login"
            element={!user ? <FranchiseLogin /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/register"
            element={
              // Allow access to /register?ref=... even when logged in (franchise referral link)
              (() => {
                const params = new URLSearchParams(window.location.search);
                const hasRef = params.get('ref');
                return (!user || hasRef) ? <Register /> : <Navigate to="/dashboard" />;
              })()
            }
          />

          {/* Policy Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/landing" element={<LandingPage />} />

          {/* Admin User Profile View - For super admin to view/edit any user */}
          <Route
            path="/admin/user/:userId"
            element={
              <ProtectedRoute requiredUserType="superadmin">
                <AdminUserProfile />
              </ProtectedRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/superadmin/*"
            element={
              <ProtectedRoute requiredUserType="superadmin">
                <Routes>
                  <Route index element={<Navigate to="dashboard" />} />
                  <Route path="dashboard" element={<SuperAdminDashboard />} />
                  <Route path="users" element={<ManageUsers />} />
                  <Route path="packages" element={<SuperAdminPackages />} />
                  <Route path="marketing" element={<MarketingPage user={user} userData={userData} />} />
                  <Route path="companies" element={<SuperAdminCompanies />} />
                  <Route path="jobs" element={<SuperAdminJobs />} />
                  <Route path="subscriptions" element={<SuperAdminDashboard />} />
                  <Route path="analytics" element={<SuperAdminDashboard />} />
                  <Route path="agents/:agentId" element={<AgentDetailsManagement />} />
                  <Route path="franchises" element={<SuperAdminFranchiseManagement />} />    
                  <Route path="approvals" element={<SuperAdminPageApproval />} />
                  {/* Add other admin routes here */}
                </Routes>
              </ProtectedRoute>
            }
          />

          {/* Team Routes */}
          <Route
            path="/team/*"
            element={
              user && userData?.userType === 'team' ? (
                <Routes>
                  <Route path="dashboard" element={<TeamDashboard user={user} userData={userData} />} />
                  <Route path="users" element={<TeamUsers />} />
                  <Route path="users/:userId" element={<TeamUsers />} />
                  <Route path="companies" element={<TeamCompanies />} />
                  <Route path="jobs" element={<TeamJobs />} />
                </Routes>
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />

          {/* Agent Routes */}
          <Route
            path="/agent/dashboard"
            element={
              user && userData?.userType === 'agent' ? (
                <AgentDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />

               <Route
            path="/franchise/dashboard"
            element={
              user && userData?.userType === 'franchise' ? (
                <FranchiseDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/users"
            element={
              user && userData?.userType === 'franchise' ? (
                <FranchiseUserList user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/add-employer"
            element={
              user && userData?.userType === 'franchise' ? (
                <FranchiseAddEmployer user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/companies"
            element={
              user && userData?.userType === 'franchise' ? (
                <FranchiseCompanies user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/jobs"
            element={
              user && userData?.userType === 'franchise' ? (
                <FranchiseJobs user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/profile"
            element={
              user && userData?.userType === 'franchise' ? (
                <FranchiseProfile user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/applications"
            element={
              user && userData?.userType === 'franchise' ? (
                <FranchiseApplications user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={<DashboardRedirect user={user} userData={userData} loading={loading} />}
          />
          <Route
            path="/dashboard/jobseeker"
            element={
              user && userData?.userType === 'jobseeker' ? (
                !userData?.paymentId ? <Navigate to="/seeker-profile" /> : <JobSeekerDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/dashboard/employer"
            element={
              user && userData?.userType === 'employer' ? (
                <EmployerDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/company-profile"
            element={
              user && userData?.userType === 'employer' ?
                <CompanyProfileManagement user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/companies"
            element={
              user && userData?.userType === 'jobseeker' && !userData?.paymentId ?
                <Navigate to="/seeker-profile" /> :
                <CompaniesList />
            }
          />
          <Route
            path="/companies/:companyId"
            element={<CompanyDetail />}
          />
          <Route
            path="/post-job"
            element={
              user && userData?.userType === 'employer' ?
                <PostJob user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/manage-jobs"
            element={
              user && userData?.userType === 'employer' ?
                <ManageJobs user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/jobs"
            element={
              user && userData?.userType === 'jobseeker' && !userData?.paymentId ?
                <Navigate to="/seeker-profile" /> :
                <Jobs user={user} userData={userData} />
            }
          />
          <Route
            path="/jobs/:jobId"
            element={<JobDetail user={user} userData={userData} />}
          />
          <Route
            path="/saved-jobs"
            element={
              user && userData?.userType === 'jobseeker' ?
                (!userData?.paymentId ? <Navigate to="/seeker-profile" /> : <SavedJobs user={user} userData={userData} />) :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/seeker-profile"
            element={
              user && userData?.userType === 'jobseeker' ?
                <SeekerProfile user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/application-history"
            element={
              user && userData?.userType === 'jobseeker' ?
                (!userData?.paymentId ? <Navigate to="/seeker-profile" /> : <ApplicationHistory user={user} userData={userData} />) :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/notification-settings"
            element={
              user && userData?.userType === 'jobseeker' ?
                (!userData?.paymentId ? <Navigate to="/seeker-profile" /> : <NotificationSettings user={user} userData={userData} />) :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/jobs/:jobId/applications"
            element={
              user && userData?.userType === 'employer' ?
                <JobApplications user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/applications"
            element={
              user && userData?.userType === 'employer' ?
                <EmployerApplications user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/packages"
            element={
              user ?
                <PackagesPage user={user} userData={userData} /> :
                <Navigate to="/login" />
            }
          />
          <Route
            path="/marketing"
            element={
              user ?
                <MarketingPage user={user} userData={userData} /> :
                <Navigate to="/login" />
            }
          />
          <Route
            path="/my-subscription"
            element={
              user ?
                <MySubscription user={user} userData={userData} /> :
                <Navigate to="/login" />
            }
          />
          <Route
            path="/database"
            element={
              user && (userData?.userType === 'employer' || userData?.userType === 'superadmin') ?
                <SeekerDatabase user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/billing-history"
            element={
              user && userData?.userType === 'employer' ?
                <BillingHistory user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />

          <Route
            path="/resources"
            element={
              user && userData?.userType === 'jobseeker' && !userData?.paymentId ?
                <Navigate to="/seeker-profile" /> :
                <Navigate to="/" />
            }
          />
          <Route
            path="/resume-builder"
            element={
              user && userData?.userType === 'jobseeker' && !userData?.paymentId ?
                <Navigate to="/seeker-profile" /> :
                <Navigate to="/" />
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;